import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { resolveFileUrl } from '../../utils/fileUrl';
import { useAuth } from '../../context/AuthContext';
import { User, Save, Loader2, Camera, MapPin, Briefcase, Phone, Mail, Calendar } from 'lucide-react';
import SearchableSelect from '../../components/ui/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const MemberProfile = () => {
  const { user, token, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [options, setOptions] = useState({
    nukhs: [],
    education: [],
    occupation: [],
    countries: [],
    statesByCountry: {},
    citiesByCountry: {},
    citiesByState: {}
  });

  const [formData, setFormData] = useState({
    fullName: '',
    fatherOrSpouseName: '',
    dateOfBirth: '',
    phone: '',
    alternateContact: '',
    familyName: '',
    nukh: '',
    address: '',
    correspondenceAddress: '',
    country: 'India',
    state: '',
    city: '',
    pincode: '',
    educationalQualification: '',
    occupation: '',
    occupationOther: '',
    workplaceAddress: '',
    designation: '',
    socialInvolvement: ''
  });

  useEffect(() => {
    fetchProfile();
    fetchOptions();
    fetchLocationOptions();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/members/me/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProfileData(data);
        setFormData({
          fullName: data.fullName || '',
          fatherOrSpouseName: data.fatherOrSpouseName || '',
          dateOfBirth: data.dateOfBirth || '',
          phone: data.phone || '',
          alternateContact: data.alternateContact || '',
          familyName: data.familyName || '',
          nukh: data.nukh || '',
          address: data.address || '',
          correspondenceAddress: data.correspondenceAddress || '',
          country: data.country || 'India',
          state: data.state || '',
          city: data.city || '',
          pincode: data.pincode || '',
          educationalQualification: data.educationalQualification || '',
          occupation: data.occupation || '',
          occupationOther: data.occupationOther || '',
          workplaceAddress: data.workplaceAddress || '',
          designation: data.designation || '',
          socialInvolvement: data.socialInvolvement || ''
        });
        setPhotoPreview(data.profilePhoto ? resolveFileUrl(data.profilePhoto) : null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/options`);
      if (response.ok) {
        const data = await response.json();
        setOptions(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchLocationOptions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/professionals/options`);
      if (response.ok) {
        const data = await response.json();
        setOptions(prev => ({
          ...prev,
          countries: data.countries || [],
          statesByCountry: data.statesByCountry || {},
          citiesByCountry: data.citiesByCountry || {},
          citiesByState: data.citiesByState || {}
        }));
      }
    } catch (error) {
      console.error('Error fetching location options:', error);
    }
  };

  const getStatesForCountry = (country) => {
    if (!country || !options.statesByCountry) return [];
    return options.statesByCountry[country] || [];
  };

  const getCitiesForState = (country, state) => {
    if (!country) return [];
    if (country === 'India' && state && options.citiesByState) {
      return options.citiesByState[state] || [];
    }
    if (options.citiesByCountry) {
      return options.citiesByCountry[country] || [];
    }
    return [];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, and WebP images are allowed');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formDataPhoto = new FormData();
      formDataPhoto.append('photo', file);

      const response = await fetch(`${BACKEND_URL}/api/members/me/photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataPhoto
      });

      if (response.ok) {
        const data = await response.json();
        setPhotoPreview(resolveFileUrl(data.photoUrl));
        toast.success('Photo updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload photo');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/members/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          fullName: formData.fullName || null,
          fatherOrSpouseName: formData.fatherOrSpouseName || null,
          dateOfBirth: formData.dateOfBirth || null,
          phone: formData.phone || null,
          alternateContact: formData.alternateContact || null,
          familyName: formData.familyName || null,
          nukh: formData.nukh || null,
          address: formData.address || null,
          correspondenceAddress: formData.correspondenceAddress || null,
          country: formData.country || null,
          state: formData.state || null,
          city: formData.city || null,
          pincode: formData.pincode || null,
          educationalQualification: formData.educationalQualification || null,
          occupation: formData.occupation || null,
          occupationOther: formData.occupationOther || null,
          workplaceAddress: formData.workplaceAddress || null,
          designation: formData.designation || null,
          socialInvolvement: formData.socialInvolvement || null
        })
      });

      if (response.ok) {
        const updatedData = await response.json();
        setProfileData(updatedData);
        if (updateUser) {
          updateUser(updatedData);
        }
        setEditMode(false);
        toast.success('Profile updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif font-bold text-3xl text-primary">My Profile</h1>
        {!editMode ? (
          <Button onClick={() => setEditMode(true)} className="bg-primary hover:bg-primary/90" data-testid="edit-profile-btn">
            Edit Profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-profile-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <Card className="p-6 md:p-8 shadow-lg">
        {/* Profile Photo and Basic Info */}
        <div className="flex flex-col md:flex-row gap-6 items-start mb-8 pb-8 border-b border-gray-200">
          <div className="relative">
            <div className="w-32 h-32 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border-4 border-primary/20">
              {photoPreview ? (
                <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User className="w-16 h-16 text-gray-400" />
              )}
            </div>
            <label className="absolute bottom-0 right-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 shadow-md">
              {uploadingPhoto ? (
                <Loader2 size={18} className="text-white animate-spin" />
              ) : (
                <Camera size={18} className="text-white" />
              )}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handlePhotoChange}
                disabled={uploadingPhoto}
                data-testid="profile-photo-upload"
              />
            </label>
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">{profileData?.fullName}</h2>
            <p className="text-primary font-medium mt-1">{profileData?.membershipId}</p>
            <div className="flex items-center gap-4 mt-3 text-gray-600">
              <div className="flex items-center gap-1">
                <Mail size={14} />
                <span className="text-sm">{profileData?.email}</span>
              </div>
              {profileData?.phone && (
                <div className="flex items-center gap-1">
                  <Phone size={14} />
                  <span className="text-sm">{profileData.phone}</span>
                </div>
              )}
            </div>
            <div className="mt-2">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                profileData?.status === 'approved' 
                  ? 'bg-green-100 text-green-700' 
                  : profileData?.status === 'pending'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {profileData?.status?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {editMode ? (
          /* Edit Form */
          <div className="space-y-6">
            {/* Personal Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Personal Details
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input name="fullName" value={formData.fullName} onChange={handleChange} className="mt-1" />
                </div>
                <div>
                  <Label>Father/Spouse Name</Label>
                  <Input name="fatherOrSpouseName" value={formData.fatherOrSpouseName} onChange={handleChange} className="mt-1" />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} className="mt-1" />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input name="phone" value={formData.phone} onChange={handleChange} className="mt-1" />
                </div>
                <div>
                  <Label>Alternate Contact</Label>
                  <Input name="alternateContact" value={formData.alternateContact} onChange={handleChange} className="mt-1" />
                </div>
                <div>
                  <Label>Family Name</Label>
                  <Input name="familyName" value={formData.familyName} onChange={handleChange} className="mt-1" />
                </div>
                <div>
                  <Label>Nukh</Label>
                  <select
                    name="nukh"
                    value={formData.nukh}
                    onChange={handleChange}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select Nukh</option>
                    {options.nukhs?.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Address Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Address Details
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Permanent Address</Label>
                  <Textarea name="address" value={formData.address} onChange={handleChange} className="mt-1" rows={2} />
                </div>
                <div className="md:col-span-2">
                  <Label>Correspondence Address</Label>
                  <Textarea name="correspondenceAddress" value={formData.correspondenceAddress} onChange={handleChange} className="mt-1" rows={2} />
                </div>
                <div>
                  <Label>Country</Label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select Country</option>
                    {options.countries?.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>State</Label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    disabled={!formData.country}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm disabled:opacity-50"
                  >
                    <option value="">Select State</option>
                    {getStatesForCountry(formData.country).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>City</Label>
                  <SearchableSelect
                    options={getCitiesForState(formData.country, formData.state)}
                    value={formData.city}
                    onChange={(value) => setFormData(prev => ({ ...prev, city: value }))}
                    placeholder="Search city..."
                    allowCustom={true}
                    disabled={!formData.state}
                    name="city"
                  />
                </div>
                <div>
                  <Label>Pincode</Label>
                  <Input name="pincode" value={formData.pincode} onChange={handleChange} className="mt-1" />
                </div>
              </div>
            </div>

            {/* Professional Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary" />
                Education & Occupation
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Educational Qualification</Label>
                  <select
                    name="educationalQualification"
                    value={formData.educationalQualification}
                    onChange={handleChange}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select Qualification</option>
                    {options.education?.map((e) => (
                      <option key={e} value={e}>{e}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Occupation</Label>
                  <select
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleChange}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select Occupation</option>
                    {options.occupation?.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                {formData.occupation === 'अन्य (Other)' && (
                  <div>
                    <Label>Other Occupation</Label>
                    <Input name="occupationOther" value={formData.occupationOther} onChange={handleChange} className="mt-1" />
                  </div>
                )}
                <div>
                  <Label>Designation</Label>
                  <Input name="designation" value={formData.designation} onChange={handleChange} className="mt-1" />
                </div>
                <div className="md:col-span-2">
                  <Label>Workplace Address</Label>
                  <Input name="workplaceAddress" value={formData.workplaceAddress} onChange={handleChange} className="mt-1" />
                </div>
                <div className="md:col-span-2">
                  <Label>Social Involvement</Label>
                  <Textarea name="socialInvolvement" value={formData.socialInvolvement} onChange={handleChange} className="mt-1" rows={2} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* View Mode */
          <div className="space-y-8">
            {/* Personal Information */}
            <div>
              <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-primary" />
                Personal Information
              </h3>
              <div className="grid md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Father/Spouse Name</p>
                  <p className="font-medium">{profileData?.fatherOrSpouseName || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date of Birth</p>
                  <p className="font-medium">{profileData?.dateOfBirth || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Alternate Contact</p>
                  <p className="font-medium">{profileData?.alternateContact || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Family Name</p>
                  <p className="font-medium">{profileData?.familyName || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Nukh</p>
                  <p className="font-medium">{profileData?.nukh || '-'}</p>
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div>
              <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-primary" />
                Address Information
              </h3>
              <div className="grid md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-500">Permanent Address</p>
                  <p className="font-medium">{profileData?.address || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-500">Correspondence Address</p>
                  <p className="font-medium">{profileData?.correspondenceAddress || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">City</p>
                  <p className="font-medium">{profileData?.city || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">State</p>
                  <p className="font-medium">{profileData?.state || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Country</p>
                  <p className="font-medium">{profileData?.country || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pincode</p>
                  <p className="font-medium">{profileData?.pincode || '-'}</p>
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div>
              <h3 className="font-semibold text-lg text-gray-900 flex items-center gap-2 mb-4">
                <Briefcase className="w-5 h-5 text-primary" />
                Education & Occupation
              </h3>
              <div className="grid md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Educational Qualification</p>
                  <p className="font-medium">{profileData?.educationalQualification || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Occupation</p>
                  <p className="font-medium">{profileData?.occupation || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Designation</p>
                  <p className="font-medium">{profileData?.designation || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-500">Workplace Address</p>
                  <p className="font-medium">{profileData?.workplaceAddress || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-500">Social Involvement</p>
                  <p className="font-medium">{profileData?.socialInvolvement || '-'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Notice */}
        <div className="mt-8 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <p className="text-sm text-orange-700">
            <strong>Note:</strong> Your email address ({profileData?.email}) cannot be changed as it is used for login.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default MemberProfile;
